import React, { useMemo, useState } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Textarea,
  Select,
  Spinner,
} from "@chakra-ui/react";
import {
  useGetTreatmentsByAppointmentId,
  useAdmissionTreatmentLogs,
  useAddAdmissionTreatmentLog,
  type AdmissionTreatmentLogEntry,
} from "../../apis/patients";

type Props = {
  admissionId: string;
  appointmentId?: string;
};

const fmt = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function AdmissionTreatmentAdminLog({
  admissionId,
  appointmentId,
}: Props) {
  // Planned treatments from the admission's appointment
  const { data: treatmentOrders = [], isLoading: isLoadingOrders } =
    useGetTreatmentsByAppointmentId(appointmentId || "");

  // Existing administration log
  const {
    data: entries = [],
    isLoading: isLoadingLog,
    isError,
  } = useAdmissionTreatmentLogs(admissionId);

  const { mutate: addEntry, isPending: isSaving } =
    useAddAdmissionTreatmentLog(admissionId);

  const [selectedTreatmentId, setSelectedTreatmentId] = useState<string>("");
  const [status, setStatus] = useState<string>("given");
  const [role, setRole] = useState<string>("nurse");
  const [notes, setNotes] = useState<string>("");

  const orderedTreatments = useMemo(
    () => (Array.isArray(treatmentOrders) ? treatmentOrders : []),
    [treatmentOrders],
  );

  const sortedEntries: AdmissionTreatmentLogEntry[] = useMemo(() => {
    if (!Array.isArray(entries)) return [];
    return [...entries].sort((a, b) => {
      const ta = new Date((a as any).administeredAt || "").getTime();
      const tb = new Date((b as any).administeredAt || "").getTime();
      return tb - ta;
    });
  }, [entries]);

  const handleAdd = () => {
    const trimmedNotes = notes.trim();

    // ✅ Require *something*: either a treatment OR notes
    if (!selectedTreatmentId && !trimmedNotes) return;

    addEntry(
      {
        treatmentId: selectedTreatmentId || undefined,
        status,
        role,
        notes: trimmedNotes || undefined,
      },
      {
        onSuccess: () => {
          setNotes("");
          // keep selectedTreatmentId so repeated doses are easy,
          // or clear it if you prefer:
          // setSelectedTreatmentId("");
        },
      },
    );
  };

  const addDisabled = isSaving || (!selectedTreatmentId && !notes.trim());

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="white">
      <Text fontWeight="bold" mb={2}>
        Treatment Administration Log
      </Text>

      {/* Existing entries */}
      {isLoadingLog ? (
        <Spinner size="sm" />
      ) : isError ? (
        <Text fontSize="sm" color="red.500">
          Failed to load treatment administration log.
        </Text>
      ) : sortedEntries.length === 0 ? (
        <Text fontSize="sm" color="gray.500" mb={3}>
          No doses recorded yet.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3} mb={4}>
          {sortedEntries.map((e: AdmissionTreatmentLogEntry) => {
            const anyRoute = (e as any).route;
            const anyFreq = (e as any).frequency;
            const anyDur = (e as any).duration;

            const hasPlanDetails = !!(anyRoute || anyFreq || anyDur);

            const treatmentLabel =
              (e as any).treatmentName ||
              (hasPlanDetails ? "TREATMENT" : "NOTE");

            return (
              <Box
                key={(e as any)._id}
                p={3}
                borderWidth="1px"
                borderRadius="md"
              >
                <HStack justify="space-between" mb={1}>
                  <HStack spacing={2}>
                    <Badge colorScheme={hasPlanDetails ? "teal" : "purple"}>
                      {treatmentLabel.toString().toUpperCase()}
                    </Badge>
                    <Badge
                      colorScheme={
                        (e as any).status === "given"
                          ? "green"
                          : (e as any).status === "missed"
                            ? "red"
                            : (e as any).status === "refused"
                              ? "orange"
                              : "yellow"
                      }
                    >
                      {(e as any).status?.toString().toUpperCase() ?? "STATUS"}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    {fmt((e as any).administeredAt)}
                  </Text>
                </HStack>

                {/* Only show plan details line if we actually have any */}
                {hasPlanDetails && (
                  <Text fontSize="sm" color="gray.700">
                    {anyRoute && (
                      <>
                        <b>Route:</b> {anyRoute}{" "}
                      </>
                    )}
                    {anyFreq && (
                      <>
                        • <b>Frequency:</b> {anyFreq}{" "}
                      </>
                    )}
                    {anyDur && (
                      <>
                        • <b>Duration:</b> {anyDur}
                      </>
                    )}
                  </Text>
                )}

                <HStack mt={1} justify="space-between">
                  <Text fontSize="xs" color="gray.600">
                    By{" "}
                    {(e as any).administeredByName ||
                      (e as any).administeredBy ||
                      "Staff"}{" "}
                    ({(e as any).role || "staff"})
                  </Text>
                </HStack>

                {(e as any).notes && (
                  <Text fontSize="sm" mt={1} whiteSpace="pre-wrap">
                    <b>Notes:</b> {(e as any).notes}
                  </Text>
                )}
              </Box>
            );
          })}
        </VStack>
      )}

      {/* Add new entry */}
      <Box mt={4}>
        <Text fontSize="sm" fontWeight="semibold" mb={2}>
          Record a dose
        </Text>

        <HStack mb={2} spacing={3} align="flex-start">
          <Select
            size="sm"
            value={selectedTreatmentId}
            onChange={(e) => setSelectedTreatmentId(e.target.value)}
            maxW="260px"
            isDisabled={isLoadingOrders || orderedTreatments.length === 0}
          >
            <option value="">Select treatment (optional)</option>
            {orderedTreatments.map((t: any) => (
              <option key={t.treatmentId} value={t.treatmentId}>
                {t.name || "Treatment"} {t.dosage ? `(${t.dosage})` : ""}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            maxW="150px"
          >
            <option value="given">Given</option>
            <option value="missed">Missed</option>
            <option value="refused">Refused</option>
            <option value="partial">Partial dose</option>
          </Select>

          <Select
            size="sm"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            maxW="150px"
          >
            <option value="nurse">Nurse</option>
            <option value="doctor">Doctor</option>
            <option value="pharmacist">Pharmacist</option>
            <option value="nutritionist">Nutritionist</option>
            <option value="lab">Lab</option>
            <option value="other">Other</option>
          </Select>
        </HStack>

        <Textarea
          size="sm"
          rows={3}
          placeholder="Notes (optional)... (you can save a notes-only entry)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <HStack mt={2} justify="flex-end">
          <Button
            size="sm"
            colorScheme="teal"
            onClick={handleAdd}
            isLoading={isSaving}
            isDisabled={addDisabled}
          >
            Add Entry
          </Button>
        </HStack>
      </Box>
    </Box>
  );
}
